import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, map } from 'rxjs';
import type { ApiResponse } from '@shared-models';
import { API_CONFIG, ApiConfig } from '../api-config';

/**
 * Payload / response del sub-recurso ubicación de sucursal.
 *
 * GET/PUT `/api/structure/branches/{branchId}/location`
 */
export interface BranchLocationApi {
  address?: string | null;
  lat?: number | null;
  lng?: number | null;
}

/** Shape UI del form de ubicación (mapa). */
export interface BranchLocationFormValue {
  address: string;
  lat: number | null;
  lng: number | null;
}

/**
 * Sub-recurso ubicación de sucursal (edit: GET + PUT).
 */
@Injectable({ providedIn: 'root' })
export class BranchLocationService {
  private readonly httpClient = inject(HttpClient);
  private readonly apiConfig = inject<ApiConfig>(API_CONFIG);

  private get apiUrl(): string {
    return `${this.apiConfig.apiUrl}/structure/branches`;
  }

  get(branchId: string): Observable<BranchLocationApi | null> {
    return this.httpClient
      .get<ApiResponse<BranchLocationApi | null>>(
        `${this.apiUrl}/${branchId}/location`,
      )
      .pipe(map((response) => response.data ?? null));
  }

  update(
    branchId: string,
    payload: BranchLocationApi,
  ): Observable<BranchLocationApi | null> {
    return this.httpClient
      .put<ApiResponse<BranchLocationApi | null>>(
        `${this.apiUrl}/${branchId}/location`,
        payload,
      )
      .pipe(map((response) => response.data ?? null));
  }

  /** API → form UI. */
  toFormValue(data: BranchLocationApi | null): BranchLocationFormValue {
    return {
      address: data?.address?.trim() || '',
      lat: toFiniteNumber(data?.lat),
      lng: toFiniteNumber(data?.lng),
    };
  }

  /** Form UI → body PUT. */
  toUpsertPayload(form: BranchLocationFormValue): BranchLocationApi {
    return {
      address: form.address?.trim() || undefined,
      lat: form.lat ?? undefined,
      lng: form.lng ?? undefined,
    };
  }

  hasPersistedData(data: BranchLocationApi | null): boolean {
    if (!data) return false;
    const lat = toFiniteNumber(data.lat);
    const lng = toFiniteNumber(data.lng);
    return !!(data.address?.trim() || (lat !== null && lng !== null));
  }
}

function toFiniteNumber(value: unknown): number | null {
  if (value === null || value === undefined || value === '') return null;
  const n = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(n) ? n : null;
}
